import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, RotateCcw, X } from "lucide-react";
import { useEffect } from "react";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import PartnerBadge from "../components/PartnerBadge";

export const FailureScreen = () => {
  const nav = useNavigate();
  const { activeTx, partner, resetDraft, setActiveTx, updateActiveTx } = useApp();

  useEffect(() => {
    if (!activeTx) {
      nav("/", { replace: true });
      return;
    }
    toast.error("Payment failed to execute");
  }, [activeTx, nav]);

  if (!activeTx) return null;

  const retry = () => {
    updateActiveTx({ status: "INIT" });
    nav("/send/processing", { replace: true });
  };

  const cancel = () => {
    resetDraft();
    setActiveTx(null);
    nav("/", { replace: true });
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col px-6 pt-8 pb-6"
      data-testid="failure-screen"
    >
      <div className="mx-auto w-20 h-20 rounded-full bg-red-50 border border-red-100 flex items-center justify-center">
        <AlertTriangle className="w-10 h-10 text-red-500" strokeWidth={2.2} />
      </div>

      <h1 className="font-display text-[26px] font-bold text-center mt-6 tracking-tight">
        Payment failed
      </h1>
      <p className="text-center text-[13px] text-gray-500 mt-2 leading-snug max-w-[300px] mx-auto">
        {partner.name} could not execute this transfer. No funds were moved. You can
        retry or cancel this payment.
      </p>

      <div className="mt-5 mx-auto">
        <PartnerBadge partner={partner.name} variant="pill" prefix="Attempted via" />
      </div>

      {/* Details */}
      <div className="mt-6 rounded-3xl border border-red-100 bg-red-50/40 p-5 space-y-2 text-[13px]">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">To</span>
          <span className="font-semibold text-gray-900">{activeTx.recipient.name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Amount</span>
          <span className="font-semibold text-gray-900">{activeTx.amount.toFixed(2)} kr</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Reference</span>
          <span className="font-mono text-[12px] text-gray-700">{activeTx.reference}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Error code</span>
          <span className="font-mono text-[12px] text-red-600">E_ROUTING_TIMEOUT</span>
        </div>
      </div>

      <div className="mt-auto space-y-2 pt-6">
        <button
          data-testid="retry-btn"
          onClick={retry}
          className="w-full bg-gray-900 text-white h-12 rounded-full font-semibold text-[14px] hover:bg-black active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4" strokeWidth={2.2} />
          Retry payment
        </button>
        <button
          data-testid="failure-cancel-btn"
          onClick={cancel}
          className="w-full bg-gray-100 text-gray-900 h-12 rounded-full font-semibold text-[14px] hover:bg-gray-200 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          <X className="w-4 h-4" strokeWidth={2.2} />
          Cancel
        </button>
      </div>
    </motion.div>
  );
};

export default FailureScreen;
